import { PathLayer, ScatterplotLayer } from '@deck.gl/layers';
import type { Layer } from '@deck.gl/core';
import type { LayerState, GridSummary, GridFlowEntry } from '@vitals/shared';
import type { RendererDef } from './registry.js';
import { CABLES, type CableDef } from './cableGeometry.js';

/** Idle (near-zero flow) cable color — dim slate, matches the "quiet" tone used
 * elsewhere in the map. */
const IDLE_RGB: [number, number, number] = [147, 164, 196];
const IDLE_ALPHA = 90; // ~0.35 * 255

/** Active-flow cable color — a hydro-blue accent. */
const ACTIVE_RGB: [number, number, number] = [0x39, 0x87, 0xe5];
const ACTIVE_ALPHA = 230;

/** Below this |MW| a cable reads as idle regardless of a stale/near-zero limit. */
const IDLE_THRESHOLD_MW = 5;

const CORE_WIDTH_MIN_PX = 1.5;
const CORE_WIDTH_MAX_PX = 5;
const GLOW_WIDTH_SCALE = 3; // glow underlayer width relative to the core
const GLOW_ALPHA = 55;

/** Terminus bullet sizing — station-dot style but larger, so the landing
 * substations read at a glance at citywide zoom. */
const TERMINAL_RADIUS_PX = 6;
const TERMINAL_RING: [number, number, number, number] = [9, 13, 22, 220]; // panel surface dark

export interface CableDatum {
  cable: CableDef;
  flow: GridFlowEntry | null;
}

/** 0 (idle) .. 1 (at/over capacity) utilization from a flow's magnitude vs. rated capacity. */
export function utilization(cable: CableDef, flow: GridFlowEntry | null): number {
  if (!flow) return 0;
  if (cable.capacityMw <= 0) return 0;
  const frac = Math.abs(flow.mw) / cable.capacityMw;
  return Math.min(1, Math.max(0, frac));
}

export function isIdle(flow: GridFlowEntry | null): boolean {
  if (!flow) return true;
  return Math.abs(flow.mw) < IDLE_THRESHOLD_MW;
}

/** Core line width in pixels, scaled by utilization between the min/max. */
export function coreWidthPx(cable: CableDef, flow: GridFlowEntry | null): number {
  const u = utilization(cable, flow);
  return CORE_WIDTH_MIN_PX + (CORE_WIDTH_MAX_PX - CORE_WIDTH_MIN_PX) * u;
}

/** Core line color: dim slate when idle, hydro-blue accent when carrying flow. */
export function coreColor(flow: GridFlowEntry | null): [number, number, number, number] {
  if (isIdle(flow)) return [...IDLE_RGB, IDLE_ALPHA];
  return [...ACTIVE_RGB, ACTIVE_ALPHA];
}

export function glowColor(flow: GridFlowEntry | null): [number, number, number, number] {
  if (isIdle(flow)) return [...IDLE_RGB, Math.round(GLOW_ALPHA * 0.6)];
  return [...ACTIVE_RGB, GLOW_ALPHA];
}

/** Join server flow entries (nullable, keyed by id) with the static cable geometry.
 * Cables with no matching flow (unknown id, or `flows: null` from a degraded/cached
 * summary) render in idle style rather than being dropped. */
export function buildCableData(flows: GridFlowEntry[] | null): CableDatum[] {
  const byId = new Map((flows ?? []).map((f) => [f.id, f]));
  return CABLES.map((cable) => ({ cable, flow: byId.get(cable.id) ?? null }));
}

export interface CableTerminalDatum extends CableDatum {
  /** A real terminus facility location — an end coordinate of the cable's path. */
  position: [number, number];
  /** The facility name at this end (landing substation or remote converter). */
  terminusName: string;
}

/**
 * Terminus bullets at every real facility end: always the landing (last path
 * point), plus the remote (first) end when it's an on-map facility rather
 * than a map-edge truncation (`remoteTerminus` present).
 */
export function buildTerminalData(data: CableDatum[]): CableTerminalDatum[] {
  return data.flatMap((d) => {
    const terminals: CableTerminalDatum[] = [
      {
        ...d,
        position: d.cable.path[d.cable.path.length - 1]!,
        terminusName: d.cable.landing,
      },
    ];
    if (d.cable.remoteTerminus) {
      terminals.push({
        ...d,
        position: d.cable.path[0]!,
        terminusName: d.cable.remoteTerminus,
      });
    }
    return terminals;
  });
}

function summaryOf(state: LayerState): GridSummary | null {
  if (!state || state.status === 'down' || !state.summary) return null;
  return state.summary as unknown as GridSummary;
}

/**
 * Renders NYISO's tracked interconnection cables as a wake-under-dots-style pair
 * of PathLayers (wide low-opacity glow underneath a narrower core), mirroring the
 * ferry wake pattern. `summary.flows` is nullable (degraded/cached summary,
 * pre-flows server build) — that renders every cable in idle style rather than
 * crashing or hiding the layer. Returns null only when the layer state itself is
 * absent or explicitly down (fuel mix / load also failed).
 */
export function buildCablesLayer(state: LayerState, _nowSec: number): Layer[] | null {
  if (!state || state.status === 'down') return null;
  const summary = summaryOf(state);
  const flows = summary?.flows ?? null;
  const data = buildCableData(flows);

  const glowLayer = new PathLayer<CableDatum>({
    id: 'grid-cables-glow',
    data,
    getPath: (d) => d.cable.path,
    getColor: (d) => glowColor(d.flow),
    getWidth: (d) => coreWidthPx(d.cable, d.flow) * GLOW_WIDTH_SCALE,
    widthUnits: 'pixels',
    widthMinPixels: CORE_WIDTH_MIN_PX * GLOW_WIDTH_SCALE,
    capRounded: true,
    jointRounded: true,
    pickable: false,
  });

  const coreLayer = new PathLayer<CableDatum>({
    id: 'grid_load',
    data,
    getPath: (d) => d.cable.path,
    getColor: (d) => coreColor(d.flow),
    getWidth: (d) => coreWidthPx(d.cable, d.flow),
    widthUnits: 'pixels',
    widthMinPixels: CORE_WIDTH_MIN_PX,
    capRounded: true,
    jointRounded: true,
    pickable: true,
    updateTriggers: {
      getColor: state.generatedAt,
      getWidth: state.generatedAt,
    },
  });

  // Terminus bullets: a station-style dot at each landing substation; hover
  // surfaces what the cable terminates at (handled in tooltip.ts).
  const terminalsLayer = new ScatterplotLayer<CableTerminalDatum>({
    id: 'grid-cable-terminals',
    data: buildTerminalData(data),
    getPosition: (d) => d.position,
    getRadius: TERMINAL_RADIUS_PX,
    radiusUnits: 'pixels',
    radiusMinPixels: 5,
    radiusMaxPixels: 10,
    getFillColor: (d) => coreColor(d.flow),
    getLineColor: TERMINAL_RING,
    getLineWidth: 1.5,
    lineWidthUnits: 'pixels',
    filled: true,
    stroked: true,
    pickable: true,
    updateTriggers: {
      getFillColor: state.generatedAt,
    },
  });

  return [glowLayer, coreLayer, terminalsLayer];
}

export const cablesRenderer: RendererDef = {
  layerId: 'grid_load',
  build: (state, nowSec) => buildCablesLayer(state, nowSec),
};
